import {sourceFileConstraints} from '@packages/domains/code-libraries/data-types';
import {
  instructionConstraints,
  isAgentInstructionPath,
  systemInstructionConstraints,
} from '@packages/domains/instructions/data-types';
import {z} from 'zod';

const isSystemInstructionPath = (path: string): boolean => {
  if (!path.startsWith(systemInstructionConstraints.pathPrefix)) {
    return false;
  }

  const unprefixedPath = path.slice(
    systemInstructionConstraints.pathPrefix.length
  );
  return isAgentInstructionPath(unprefixedPath);
};

export const instructionPathZod = z
  .string()
  .min(1)
  .max(instructionConstraints.filePath.maxLength)
  .refine(isAgentInstructionPath, {
    message:
      'Agent instructions must use relative rules/*.md, rules/*.mdc, skill-name/SKILL.md, or skill-name/<companion>.md paths.',
  });

export const readInstructionPathZod = z
  .string()
  .min(1)
  .max(instructionConstraints.filePath.maxLength)
  .refine(
    (path) => isAgentInstructionPath(path) || isSystemInstructionPath(path),
    {
      message:
        'Agent instructions must use relative rules/*.md, rules/*.mdc, skill-name/SKILL.md, skill-name/<companion>.md, or wf/<path> paths.',
    }
  );

const isAllowedMarkdownControlCharacter = (charCode: number): boolean =>
  charCode === 9 || charCode === 10 || charCode === 13;

const normalizeDisallowedControlCharacters = (value: string): string => {
  let normalized = '';

  for (let index = 0; index < value.length; index += 1) {
    const char = value.charAt(index);
    const charCode = value.charCodeAt(index);
    if (
      (charCode < 32 || charCode === 127) &&
      !isAllowedMarkdownControlCharacter(charCode)
    ) {
      normalized += ' ';
    } else {
      normalized += char;
    }
  }

  return normalized;
};

export const instructionMarkdownZod = z
  .string()
  .max(instructionConstraints.content.maxLength, {
    message: 'Agent instruction markdown must be text content.',
  })
  .transform(normalizeDisallowedControlCharacters);

export const instructionDescriptionZod = z
  .string()
  .max(sourceFileConstraints.description.maxLength)
  .optional()
  .describe('Optional short description shown in instruction lists.');

export const instructionIsDraftZod = z
  .boolean()
  .optional()
  .describe('Whether the instruction should be saved as a draft.');
